import { Box, Button, CircularProgress, IconButton, styled, Typography, useTheme } from '@mui/material';
import React, { useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import FlexBox from 'components/flex-box';
import LoadingSpinner from 'components/loading-spinner';

import Drawer, { DrawerHandle } from 'components/drawer';
import { Inbox } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import CreateOrderForm, {
  DefaultFormValues,
} from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/orders/form/create-order-form';
import { useCreateOrder } from 'views/order-feed/features/my-supply-plan/hooks/use-create-order';
import { CreateOrderFormValues } from 'views/order-feed/features/my-supply-plan/types/create-order-form-values.type';
import { OFD2Params } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/ofd-2-params.type';
import { useGoogleTagManager } from 'views/order-feed/features/my-supply-plan/hooks/use-google-tag-manager';
import { OrderType, SupplyPlanType } from 'views/order-feed/features/my-supply-plan/types/google-tag-manager/order-feed-events.type';

type Props = {};

const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan.orders';

const CustomButton = styled(Button)(({ theme }) => ({
  border: `1px solid white`,
  color: 'white',
  '&:hover': {
    backgroundColor: 'transparent',
    color: 'white',
    border: `1px solid white`,
  },
}));

const ModalTitle: React.FC<{}> = ({}) => {
  const intl = useIntl();
  const theme = useTheme();

  return (
    <Box display={'flex'} justifyContent={'center'} alignItems={'center'} gap={3}>
      <Box>
        <Typography fontWeight={'bold'}>
          {intl.formatMessage({
            id: `${PAGE_NAME_ID}.list.createOrderBtn`,
            defaultMessage: '',
          })}
        </Typography>
      </Box>
      <IconButton sx={{ backgroundColor: theme.palette.common.white, cursor: 'unset' }} disableRipple disableTouchRipple size="small">
        <Inbox color="black" size={17} />
      </IconButton>
    </Box>
  );
};

const CreateOrderDialog: React.FC<Props> = () => {
  const drawerRef = useRef<DrawerHandle>(null);
  const { buildOrderObject, createOFDOrder } = useCreateOrder();
  const queryClient = useQueryClient();
  const { createOrderFeedEvent } = useGoogleTagManager();

  const params = useParams<OFD2Params>();

  const defaultFormValues: DefaultFormValues = useMemo(
    () => ({
      dispatchDate: '',
      vessel: '',
      plantId: '',
      siteErpId: params.siteErpId,
      orderLines: [
        {
          itemNumber: '',
          orderQuantity: 0,
        },
      ],
    }),
    []
  );

  const handleSubmit = (data: CreateOrderFormValues) => {
    createOFDOrder.mutate(
      { data: buildOrderObject(data) },
      {
        onSuccess: () => {
          [['getOrderAssortment'], ['getCustomerAssortment'], ['getRecommendationAssortment']].forEach((queryKey) => {
            queryClient.invalidateQueries({ queryKey });
          });
          drawerRef.current?.close();
          createOrderFeedEvent('create', {
            orderType: OrderType.OPEN,
            supplyPlanType: SupplyPlanType.OFD2,
          });
        },
      }
    );
  };

  return (
    <Drawer
      ref={drawerRef}
      title={<ModalTitle />}
      button={
        <CustomButton size="small" variant="outlined">
          <FormattedMessage id={`${PAGE_NAME_ID}.list.createOrderBtn`} values={{ type: '' }} />
        </CustomButton>
      }>
      <Box display={'flex'} flexDirection={'column'} padding={5} width={650}>
        <Box>
          {false && <LoadingSpinner overlay />}
          <FlexBox gap={4}>
            <CreateOrderForm onSubmit={handleSubmit} defaultFormValues={defaultFormValues} showOrderDetails>
              <Box display={'flex'} justifyContent={'flex-end'}>
                <Button
                  startIcon={createOFDOrder.isPending ? <CircularProgress color="inherit" size={18} /> : null}
                  type="submit"
                  disabled={createOFDOrder.isPending}>
                  <FormattedMessage id={`${PAGE_NAME_ID}.list.createOrderBtn`} />
                </Button>
              </Box>
            </CreateOrderForm>
          </FlexBox>
        </Box>
      </Box>
    </Drawer>
  );
};

export default CreateOrderDialog;
